import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, OnInit, output, signal } from '@angular/core';
import { Button } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { OrganizationFormComponent } from "../../forms/organization-form/organization-form";
import { AbstractComponent, LabelComponent } from '@spike/scaffolds';
import { ModelDynamicFormComponent } from "../../forms/model-dynamic-form/model-dynamic-form";
import { FormFactoryService } from '../../../shared/services/form-factory.service';
import { EtiquetteStoreService } from '../../../configuration/services/etiquette-store.service';
import { ModelStoreService } from '../../../configuration/services/model-store.service';
import { OrganizationStoreService } from '../../services/organization-store.service';
import { Checkbox } from "primeng/checkbox";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EntityApiEnum, EtiquetteApiEnum, ModelApiEnum } from 'src/app/config/entity-api.enum';
import { Machine } from '../../models/machine.model';
import { Employee } from '../../models/employee.model';
import { EntityModelField } from 'src/app/features/configuration/models/entity-model-field.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'spike-organization-side-panel',
  providers:[],
  imports: [CommonModule, FormsModule ,Button, Drawer, OrganizationFormComponent, ModelDynamicFormComponent, LabelComponent, Checkbox],
  templateUrl: './organization-side-panel.html'
})
export class OrganizationSidePanelComponent extends AbstractComponent{
  //inject  
  private readonly formFactory  = inject(FormFactoryService);
  private readonly etiquetteStoreService = inject(EtiquetteStoreService);
  private readonly modelStoreService = inject(ModelStoreService);
  public readonly organizationStore = inject(OrganizationStoreService);

  //input
  entityApi = input.required<EntityApiEnum>();
  modelApi = input.required<ModelApiEnum>();
  etiquetteApi = input.required<EtiquetteApiEnum>();
  entityKey = input.required<string>();
  isDialogVisible = model.required<boolean>();
  dataId = input<number>();

  //output
  onClose = output<void>();
  onSave = output<void>();


  //state
  entityApiEnum = EntityApiEnum;
  isFormView = signal<boolean>(false);
  employeeList = computed(()=>this.organizationStore.getList(EntityApiEnum.EMPLOYEE));
  machineList = computed(() => this.organizationStore.getList(EntityApiEnum.MACHINE));

  machineListShowed = computed(() => {
    const freshData = this.freshData();
    const allMachines = this.machineList()(); 
    if (!allMachines) return [];
    const selectedIds = new Set(freshData?.machines?.map((m: Machine) => m.machineId) || []);
    return allMachines.map((machine: Machine) => ({
        ...machine,
        selected: selectedIds.has(machine.machineId)
    }));
  });

  employeeListShowed = computed(() => {
    const freshData = this.freshData();
    const allEmployees = this.employeeList()(); 
    if (!allEmployees) return [];
    const selectedIds = new Set(freshData?.employees?.map((e: Employee) => e.employeeId) || []);
    return allEmployees.map((employee: Employee) => ({
        ...employee,
        selected: selectedIds.has(employee.employeeId)
    }));
  });


  public readonly freshData=computed(()=>{
    const found = this.organizationStore.findReactive<any>(this.entityApi(),this.entityKey(), this.dataId());
    return found();
  })

  public readonly dialogHeader= computed (()=>{
    const inputData = this.dataId();
    const isFormView = this.isFormView();
    if(!isFormView) return 'Dettaglio';
    else return inputData ? 'Modifica' : 'Nuovo';
  });

  private readonly jsonInfo = computed(()=>{
    const currentData = this.freshData();
    if (currentData && currentData.jsonInfo){
      const js = JSON.parse(currentData.jsonInfo)
      return js || {}
    }
    else return {};
  });


  public readonly fixedForm = computed(()=>{
    const currentData = this.freshData();
    const entityApi = this.entityApi();
    return this.formFactory.createForm(entityApi, currentData);
  });



  public readonly entityEtiquetteList = this.etiquetteStoreService.getReactiveList(this.etiquetteApi);
  public readonly entityModelList = this.modelStoreService.getReactiveList(this.modelApi);

  public readonly entityModelFields = computed(()=>{
    const entityModel = this.entityModelList();    
    if(entityModel && entityModel[0]){
      const jsonschema:any = JSON.parse(entityModel[0].jsonSchema);
      return (jsonschema["model"] || []) as EntityModelField[];
    }
    return [];
   });

  public readonly dynamicForm = computed(()=>{
    const jsonInfo = this.jsonInfo();
    const entityModelFields = this.entityModelFields();      
    return this.formFactory.createDynamicForm(entityModelFields, jsonInfo);
  });
  
  public readonly etiquette = computed(()=>{
    const data = this.freshData();
    const etiquetteList = this.entityEtiquetteList();
    return etiquetteList.find(x=> x.etiquetteId === data?.etiquetteId);
  })



  constructor(){
    super();
    effect(()=>{
      const dataId = this.dataId();
      if(!dataId) this.isFormView.set(true)
    })
    effect(()=>{
      const isFormView = this.isFormView();
      const freshData = this.freshData();
      
      if (!isFormView) {
          this.resetForm(freshData);
          this.fixedForm().disable();
          this.dynamicForm().disable();
      } else {
          this.fixedForm().enable();
          this.dynamicForm().enable();
      }
      this.fixedForm().markAsPristine();
      this.fixedForm().markAsUntouched();
      this.dynamicForm().markAsPristine();
      this.dynamicForm().markAsUntouched();
    });


    this.etiquette = computed(()=>{
      const data = this.freshData();
      const etiquetteList = this.entityEtiquetteList();
      return etiquetteList.find(x=> x.etiquetteId === data?.etiquetteId);
    })

  }
   private resetForm(data:any): void {
    if (data) {
      this.fixedForm().patchValue(data);
      this.dynamicForm().patchValue( JSON.parse(data.jsonInfo));
    }
  }

  async onDeleteClicked(){
    await this.organizationStore.delete(this.entityApi(), this.freshData());
    this.onSave.emit();
    this.onClose.emit();
  }

  async onRestoreClicked(){
    await this.organizationStore.restore(this.entityApi(), this.freshData());
    this.onSave.emit();
  }
  
  async onSaveClicked(){
    this.fixedForm().markAllAsTouched();
    this.dynamicForm().markAllAsTouched();
    const machineList = this.machineListShowed();
    const employeeList = this.employeeListShowed();
    if(this.fixedForm().valid && this.dynamicForm().valid){
      const toSaveData: any = {
        ...this.fixedForm().value,
        [this.entityKey()]: this.dataId(),
        jsonInfo:JSON.stringify(this.dynamicForm().value),
        machines: machineList.filter((x:Machine)=>x.selected),
        employees: employeeList.filter((x:Employee)=>x.selected)
      };
      console.log('Data to save:', toSaveData); 
      await this.organizationStore.save(this.entityApi(), this.entityKey(), toSaveData);
      this.onSave.emit();
      if(!this.dataId()){
        this.onClose.emit();
      }else{
        this.isFormView.set(false);
      }
    }
  }

  public closeDialog(){
    this.isDialogVisible.set(false);
    this.onClose.emit();
  }

}